import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  type IntercomProviderProps,
  IntercomProvider as _IntercomProvider,
  useIntercom as _useIntercom,
} from 'react-use-intercom';

const INTERCOM_APP_ID = 'ec8spyh1';

type HelperVisibility = {
  userId: string | undefined;
  visible: boolean;
};

type C = {
  unreadMessagesCount: number;
  setUnreadMessagesCount: (value: number) => void;
  isBooted: boolean;
  setIsBooted: (value: boolean) => void;
  userHash: string | undefined;
  setUserHash: (value: string | undefined) => void;
  helperVisibility: HelperVisibility;
  setHelperVisibility: (value: HelperVisibility) => void;
};

export const intercomContext = createContext<C>({
  unreadMessagesCount: 0,
  setUnreadMessagesCount: () => undefined,
  isBooted: false,
  setIsBooted: () => undefined,
  userHash: undefined,
  setUserHash: () => undefined,
  helperVisibility: { userId: undefined, visible: false },
  setHelperVisibility: () => undefined,
});

export function IntercomProvider({
  children,
  ...props
}: PropsWithChildren<Omit<IntercomProviderProps, 'appId'>>) {
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [isBooted, setIsBooted] = useState(false);
  const [userHash, setUserHash] = useState<string | undefined>(undefined);
  const [helperVisibility, setHelperVisibility] = useState<HelperVisibility>({
    userId: undefined,
    visible: false,
  });
  const [messengerOpen, setMessengerOpen] = useState(false);

  const value = useMemo<C>(
    () => ({
      unreadMessagesCount,
      setUnreadMessagesCount,
      isBooted,
      setIsBooted,
      userHash,
      setUserHash,
      helperVisibility,
      setHelperVisibility,
    }),
    [unreadMessagesCount, isBooted, userHash, helperVisibility]
  );

  return (
    <_IntercomProvider
      appId={INTERCOM_APP_ID}
      onUnreadCountChange={setUnreadMessagesCount}
      {...props}
      onShow={() => {
        setMessengerOpen(true);
        props.onShow?.();
      }}
      onHide={() => {
        setMessengerOpen(false);
        props.onHide?.();
      }}
    >
      <intercomContext.Provider value={value}>
        {/* The closed messenger frame stays in the page, invisible, and takes clicks meant
            for the buttons below it, such as the statements bulk action bar (PRO-19767).
            With the helper hidden, Intercom can report the messenger as shown while the frame
            stays invisible; every real open shows the helper first (PRO-19975). */}
        {(!messengerOpen || !helperVisibility.visible) && (
          <style>
            {
              '.intercom-messenger-frame, iframe[name="intercom-messenger-frame"] { pointer-events: none !important; }'
            }
          </style>
        )}
        {children}
      </intercomContext.Provider>
    </_IntercomProvider>
  );
}

export const useIntercom = () => {
  const intercom = _useIntercom();
  const {
    unreadMessagesCount,
    setUnreadMessagesCount,
    isBooted,
    setIsBooted,
    userHash,
    setUserHash,
    helperVisibility,
    setHelperVisibility,
  } = useContext(intercomContext);

  const setHelperVisible = useCallback(
    (visible: boolean) => {
      if (!isBooted) return;

      if (helperVisibility.userId) {
        window.localStorage.setItem(
          `intercom.helperVisible.${helperVisibility.userId}`,
          JSON.stringify(visible)
        );
      }
      if (!visible) intercom.hide();
      intercom.hideNotifications(!visible);
      intercom.update({ hideDefaultLauncher: !visible });
      setHelperVisibility({ ...helperVisibility, visible });
    },
    [
      isBooted,
      helperVisibility,
      intercom.hide,
      intercom.hideNotifications,
      intercom.update,
      setHelperVisibility,
    ]
  );

  return {
    unreadMessagesCount,
    setUnreadMessagesCount,
    ...intercom,
    isBooted,
    helperVisible: helperVisibility.visible,
    setHelperVisible,
    boot: useCallback(
      (...args: Parameters<typeof intercom.boot>) => {
        const newUserHash = args[0]?.userHash;
        if (!isBooted || userHash !== newUserHash) {
          const userId = args[0]?.userId;
          const savedVisibility = userId
            ? window.localStorage.getItem(`intercom.helperVisible.${userId}`)
            : null;
          const visible: unknown =
            savedVisibility === null
              ? !args[0]?.hideDefaultLauncher
              : JSON.parse(savedVisibility);
          if (typeof visible !== 'boolean') {
            throw new Error('Invalid stored support helper visibility');
          }

          // reboot intercom if userHash has changed
          if (userHash !== newUserHash) intercom.shutdown();
          intercom.boot({
            ...args[0],
            hideDefaultLauncher: !visible,
            hideNotifications: !visible,
          });

          setIsBooted(true);
          setUserHash(newUserHash);
          setHelperVisibility({ userId, visible });
        }
      },
      [
        intercom.boot,
        intercom.shutdown,
        isBooted,
        setIsBooted,
        userHash,
        setUserHash,
        setHelperVisibility,
      ]
    ),
    shutdown: useCallback(
      (...args: Parameters<typeof intercom.shutdown>) => {
        if (isBooted) {
          intercom.shutdown(...args);
          setIsBooted(false);
        }
      },
      [intercom.shutdown, isBooted, setIsBooted]
    ),
    hardShutdown: useCallback(() => {
      intercom.hardShutdown();
      setIsBooted(false);
    }, [intercom.hardShutdown, setIsBooted]),
    show: useCallback(() => {
      if (!helperVisibility.visible) setHelperVisible(true);
      intercom.show();
    }, [helperVisibility.visible, setHelperVisible, intercom.show]),
    showNewMessage: useCallback(
      (...args: Parameters<typeof intercom.showNewMessage>) => {
        if (!helperVisibility.visible) setHelperVisible(true);
        intercom.showNewMessage(...args);
      },
      [helperVisibility.visible, setHelperVisible, intercom.showNewMessage]
    ),
    showArticle: useCallback(
      (...args: Parameters<typeof intercom.showArticle>) => {
        if (!helperVisibility.visible) setHelperVisible(true);
        intercom.showArticle(...args);
      },
      [helperVisibility.visible, setHelperVisible, intercom.showArticle]
    ),
    update: useCallback(
      (...args: Parameters<typeof intercom.update>) => {
        if (isBooted) intercom.update(...args);
      },
      [intercom.update, isBooted]
    ),
    trackEvent: useCallback(
      (...args: Parameters<typeof intercom.trackEvent>) => {
        if (isBooted) intercom.trackEvent(...args);
      },
      [intercom.trackEvent, isBooted]
    ),
  };
};
